/* eslint-disable no-case-declarations */
import { ContentState, convertFromHTML, EditorState } from 'draft-js';
import { IError } from '../../../../base/api';
import { __t } from '../../../../base/utils/t';
import { City } from '../../../../base/interfaces/config';
import { PublishVacancyRequestParams, ValidateVacancyRequestParams } from '../api/interfaces';
import { VACANCY_DESCRIPTION_MAX_LENGTH, VACANCY_NAME_MAX_LENGTH } from '../constants';
import { Fields } from '../redux/slices/vacancyFormSlice';

export const mapTagCheckboxToId = (fieldNames: string[], fields: Fields): number[] => {
    const checkedFields = fieldNames.filter((fieldName) => fields[fieldName as keyof typeof fields]);
    if (!checkedFields.length) {
        return [];
    }

    return checkedFields.map((fieldName) => {
        switch (fieldName) {
            case 'suitableForMen':
                return 1;
            case 'suitableForWomen':
                return 2;
            case 'suitableForCouple':
                return 3;
            case 'isAccomodationProvided':
                return 4;
            case 'isFoodProvided':
                return 5;
            case 'isTransferProvided':
                return 6;
            case 'isClothesProvided':
                return 7;
            case 'isExperienceNeeded':
                return 9;
            case 'isPassportOnly':
                return 10;
            case 'suitableForStudents':
                return 12;
            case 'isHotVacancy':
                return 11;
            case 'isSeasonalWork':
                return 13;
            case 'isRemoteWork':
                return 14;
            case 'isQuickApplyAvailable':
                return 15;
            default:
                console.error(`Unsupported tag fieldname ${fieldName} provided`);
                return -1;
        }
    });
};

export const mapTagIdToCheckbox = (tagIds: number[]): string[] =>
    tagIds.map((tagId) => {
        switch (tagId) {
            case 1:
                return 'suitableForMen';
            case 2:
                return 'suitableForWomen';
            case 3:
                return 'suitableForCouple';
            case 4:
                return 'isAccomodationProvided';
            case 5:
                return 'isFoodProvided';
            case 6:
                return 'isTransferProvided';
            case 7:
                return 'isClothesProvided';
            case 9:
                return 'isExperienceNeeded';
            case 10:
                return 'isPassportOnly';
            case 12:
                return 'suitableForStudents';
            case 11:
                return 'isHotVacancy';
            case 13:
                return 'isSeasonalWork';
            case 14:
                return 'isRemoteWork';
            case 15:
                return 'isQuickApplyAvailable';
            default:
                console.error(`Unsupported tagId ${tagId} provided`);
                return '';
        }
    });

export const isTagChecked = (tagNames: string[], tag: string) => tagNames.includes(tag);

export const validateOnClient = (fields: Fields, step: number): IError | null => {
    switch (step) {
        case 1:
            if (fields.name.length > VACANCY_NAME_MAX_LENGTH) {
                return { name: [__t('Превышен лимит символов')] };
            }
            if (
                !fields.suitableForMen &&
                !fields.suitableForWomen &&
                !fields.suitableForCouple &&
                !fields.suitableForStudents
            ) {
                return { suitableFor: [__t('Выберите хотя бы один вариант')] };
            }
            break;
        case 3:
            const blocksFromHTML = convertFromHTML(fields.description);
            const initialState = ContentState.createFromBlockArray(
                blocksFromHTML.contentBlocks,
                blocksFromHTML.entityMap,
            );
            const editorState = EditorState.createWithContent(initialState);
            const descriptionLength = editorState.getCurrentContent().getPlainText('').length;
            if (descriptionLength > VACANCY_DESCRIPTION_MAX_LENGTH) {
                return { description: [__t('Превышен лимит символов')] };
            }
            break;
    }
    return null;
};

export const prepareDataByStep = (fields: Fields, step: number): ValidateVacancyRequestParams => {
    switch (step) {
        case 1:
            return {
                name: fields.name,
                workersCount: parseInt(fields.workersCount),
                tagIds: mapTagCheckboxToId(
                    [
                        'suitableForMen',
                        'suitableForWomen',
                        'suitableForCouple',
                        'suitableForStudents',
                        'isQuickApplyAvailable',
                    ],
                    fields,
                ),
                specialities: fields.specialities,
                specialityIds: fields.specialities.map((s) => s.id),
                areaId: fields.areas[0]?.id as unknown as number,
                city: fields.city as unknown as City,
                cityId: fields.city?.id || (null as unknown as number),
                countryId: fields.city?.country.id as unknown as number,
            };

        case 2:
            return {
                hoursPerMonthFrom: parseFloat(fields.hoursPerMonthFrom),
                hoursPerMonthTo: parseFloat(fields.hoursPerMonthTo),
                rateFrom: parseFloat(fields.rateFrom),
                rateTo: parseFloat(fields.rateTo),
                rateType: fields.rateType,
                currencyId: fields.currencyId,
            };

        case 3:
            return {
                tagIds: mapTagCheckboxToId(
                    [
                        'suitableForMen',
                        'suitableForWomen',
                        'suitableForCouple',
                        'suitableForStudents',
                        'isAccomodationProvided',
                        'isFoodProvided',
                        'isTransferProvided',
                        'isClothesProvided',
                        'isExperienceNeeded',
                        'isPassportOnly',
                        'isHotVacancy',
                        'isSeasonalWork',
                        'isRemoteWork',
                    ],
                    fields,
                ),
                requiredLanguageId: fields.requiredLanguageId as unknown as number,
                description: fields.description,
            };
        default:
            console.error(`Invalid step ${step} provided!`);
            return {};
    }
};

export const prepareDataToUpload = (fields: Fields): PublishVacancyRequestParams => {
    return {
        name: fields.name,
        specialities: fields.specialities,
        specialityIds: fields.specialities.map((s) => s.id),
        areaId: fields.areas[0]?.id as unknown as number,
        workersCount: parseInt(fields.workersCount),
        city: fields.city as unknown as City,
        cityId: fields.city?.id as unknown as number,
        countryId: fields.city?.country.id as unknown as number,
        tagIds: mapTagCheckboxToId(
            [
                'suitableForMen',
                'suitableForWomen',
                'suitableForCouple',
                'isAccomodationProvided',
                'isFoodProvided',
                'isTransferProvided',
                'isClothesProvided',
                'isExperienceNeeded',
                'isPassportOnly',
                'suitableForStudents',
                'isHotVacancy',
                'isSeasonalWork',
                'isRemoteWork',
                'isQuickApplyAvailable',
            ],
            fields,
        ),
        currencyId: fields.currencyId,
        hoursPerMonthFrom: parseFloat(fields.hoursPerMonthFrom),
        hoursPerMonthTo: parseFloat(fields.hoursPerMonthTo),
        rateFrom: parseFloat(fields.rateFrom),
        rateTo: parseFloat(fields.rateTo),
        rateType: fields.rateType,
        requiredLanguageId: fields.requiredLanguageId as unknown as number,
        description: fields.description,
    };
};
